import { DrawerOpenEvent } from '@theme/theme-drawer';
import { prefersReducedMotion } from '@theme/utilities';

class FreeShippingProgress extends HTMLElement {
  static observedAttributes = ['data-total', 'data-threshold', 'data-animate', 'hidden'];

  #previous;
  #pending = false;
  #queued = false;
  #drawer;
  #animations = [];

  connectedCallback() {
    this.#previous = this.#state();
    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
  }

  disconnectedCallback() {
    this.#drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    this.#pending = false;
    this.#cancelAnimations();
  }

  attributeChangedCallback() {
    if (!this.isConnected || this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      if (this.isConnected) this.#sync();
    });
  }

  #state() {
    const total = Number(this.dataset.total);
    const threshold = Number(this.dataset.threshold);
    return { total, threshold, eligible: !this.hidden && Number.isFinite(total) && threshold > 0 && Number.isFinite(threshold) };
  }

  #sync() {
    const current = this.#state();
    const previous = this.#previous;
    this.#previous = current;

    if (!current.eligible || current.total < current.threshold || this.dataset.animate !== 'true' || prefersReducedMotion()) {
      this.#pending = false;
      this.#cancelAnimations();
      return;
    }

    if (previous?.eligible && previous.threshold === current.threshold && previous.total < current.threshold) {
      this.#pending = true;
    }
    this.#celebrate();
  }

  #onDrawerOpen = async () => {
    const opening = this.#drawer?.querySelector('dialog')?.getAnimations() ?? [];
    await Promise.allSettled(opening.map(animation => animation.finished));
    this.#celebrate();
  };

  #celebrate() {
    if (!this.#pending || !this.isConnected) return;
    if (this.#drawer && !this.#drawer.querySelector('dialog')?.open) return;
    this.#pending = false;
    if (this.hidden || this.dataset.animate !== 'true' || prefersReducedMotion()) return;

    this.#cancelAnimations();
    const check = this.querySelector('[data-shipping-check]');
    const sparkles = this.querySelector('[data-shipping-sparkles]');
    if (check) this.#animations.push(check.animate([
      { transform: 'scale(0.6)' },
      { transform: 'scale(1.18)', offset: 0.65 },
      { transform: 'scale(1)' },
    ], { duration: 520, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }));
    if (sparkles) this.#animations.push(sparkles.animate([
      { opacity: 0, transform: 'scale(0.55)' },
      { opacity: 1, transform: 'scale(1)', offset: 0.35 },
      { opacity: 0, transform: 'scale(1.35)' },
    ], { duration: 700, easing: 'ease-out' }));
  }

  #cancelAnimations() {
    for (const animation of this.#animations) animation.cancel();
    this.#animations = [];
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
